import React, {startTransition, Suspense, useCallback, useEffect, useMemo, useState} from 'react';
import {UserLoggedIn} from '../../hooks/isUserLoggedIn';
// import './ShowRoomStyles.css';
import {useDispatch, useSelector} from "react-redux";
import {addToCart} from "../../features/cart/cartSlice";
import {useNavigate} from "react-router-dom";
import {nanoid} from "@reduxjs/toolkit";
import {fetchCabinets} from '../../features/showroom/showroomSlice';
import {useFilteredCabinets, useFilters} from './hooks/showroomHooks';
import memoizedCategories from './memoizedCategories';
import {fetchPresets} from '../../features/savedSettings/savedSettingsActions';
import {setSelectedSettingId} from '../../features/savedSettings/savedSettingsSlice';
import {shuffleArray} from './helpers';
import LoadingPage from "../Loading/LoadingPage";
import './styles/ShowRoomLayout.css'
import './styles/SkeletonLoading.css'
import './styles/Pagination.css'
import {useCaptureActivity} from "../../hooks/useCaptureActivity";
import SEO from '../../seo/SEO';
import {getKeywordsForPage} from "../../seo/seoKeywords";

const FilterPanel = React.lazy(() => import('./FilterPanel'));
const SearchBar = React.lazy(() => import('./SearchBar'));
const CabinetCard = React.lazy(() => import('./CabinetCard'));
const Pagination = React.lazy(() => import('./Pagination'));
const PresetStylesBar = React.lazy(() => import('./PresetStylesBar/PresetStylesBar'));

const CARDS_PER_PAGE = 28;
const CACHE_DURATION = 10 * 60 * 1000; // 5 minutes cache

const FilterPanelSkeleton = React.memo(() => (
    <div className="filter-panel filter-panel-skeleton" style={{height: '600px', minHeight: '600px'}}>
        <div className="filter-panel-header" style={{height: '80px'}}>
            <div className="filter-panel-skeleton-title"></div>
            <div className="filter-panel-divider"></div>
        </div>
        <div className="filter-panel-content" style={{height: '520px', overflow: 'hidden'}}>
            {[...Array(6)].map((_, i) => (
                <div key={i} className="filter-category" style={{height: '80px', marginBottom: '8px'}}>
                    <div className="filter-skeleton-category"></div>
                    <div className="filter-skeleton-items">
                        {[...Array(3)].map((_, j) => (
                            <div key={j} className="filter-skeleton-item"></div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    </div>
));

const ShowRoomComponent = () => {
    const userLoggedIn = UserLoggedIn();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const keywords = getKeywordsForPage('showroom');


    const [randomizedCabinets, setRandomizedCabinets] = useState([]);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [quantities, setQuantities] = useState({});
    const [cardWidth, setCardWidth] = useState(280);
    const [clientIp, setClientIp] = useState(null);
    const [isFilterPanelLoaded, setIsFilterPanelLoaded] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [searchedProducts, setSearchedProducts] = useState([]);

    const {cabinets: allCabinets, loading, error, lastFetched} = useSelector(state => state.showroom);
    const email = useSelector(state => state.user.user.email);
    const dataLoaded = !loading && !error;
    const {captureActivity, captureVisit} = useCaptureActivity();

    const presetSettings = useSelector(state => state.savedSettings.presets || []);
    const presetsLoading = useSelector(state => state.savedSettings.isLoading);
    const reduxSelectedSettingId = useSelector(state => state.savedSettings.selectedSettingId);
    const [localSelectedPresetId, setLocalSelectedPresetId] = useState(null);
    const storedIp = useSelector((state) => state.user.ipAddress);


    // useEffect(() => {
    //     const fetchClientIp = async () => {
    //         try {
    //             const res = await fetch('https://api.ipify.org?format=json');
    //             const data = await res.json();
    //             setClientIp(data.ip);
    //             dispatch(setIpAddress(data.ip))
    //             captureVisit({
    //                 ipAddress: data.ip,
    //                 email: email,
    //             })
    //         } catch (error) {
    //             console.error('Failed to retrieve client IP:', error);
    //         }
    //     };
    //     fetchClientIp();
    // }, [storedIp, dispatch]);


    useEffect(() => {
        if (reduxSelectedSettingId) {
            setLocalSelectedPresetId(reduxSelectedSettingId);
        }
    }, [reduxSelectedSettingId]);

    useEffect(() => {
        window.scrollTo({top: 0, behavior: "instant"});
    }, []);

    const {
        selectedFilters,
        setSelectedFilters,
        handleFilterSelection,
        handleCategoryClick,
        hasCabinetsWithFilterTag,
        isFilterSelected,
        isCategoryExpanded
    } = useFilters(allCabinets);

    const [presetTagToNameMap, setPresetTagToNameMap] = useState({});
    const [presetNameToTagMap, setPresetNameToTagMap] = useState({});

    const filteredCabinets = useFilteredCabinets(
        allCabinets,
        selectedFilters,
        searchQuery,
        isFirstLoad,
        randomizedCabinets
    );

    const presetStylesCategory = useMemo(() => {
        return memoizedCategories.find(cat => cat.category === 'Preset Styles');
    }, []);

    // Optimized cabinet fetching - only fetch if needed
    useEffect(() => {
        const shouldFetchCabinets = () => {
            // If no cabinets exist, fetch them
            if (allCabinets.length === 0) {
                return true;
            }

            // If cache is expired, fetch fresh data
            if (lastFetched && (Date.now() - lastFetched > CACHE_DURATION)) {
                return true;
            }

            // Otherwise, use existing data
            return false;
        };

        if (shouldFetchCabinets()) {
            dispatch(fetchCabinets(email || null));
        }
    }, [email, dispatch, allCabinets.length, lastFetched]);

    useEffect(() => {
        if (presetSettings.length === 0 && !presetsLoading) {
            dispatch(fetchPresets());
        }
    }, [dispatch, presetSettings.length, presetsLoading]);

    useEffect(() => {
        if (presetStylesCategory && presetSettings.length > 0) {
            const tagToName = {};
            const nameToTag = {};

            presetStylesCategory.subcategories.forEach(category => {
                category.subcategories.forEach(subcategory => {
                    if (subcategory.tags && subcategory.tags.length > 0) {
                        const tag = subcategory.tags[0];
                        tagToName[tag] = subcategory.displayName;
                        nameToTag[subcategory.displayName.toLowerCase()] = tag;
                    }
                });
            });

            setPresetTagToNameMap(tagToName);
            setPresetNameToTagMap(nameToTag);

            verifyAndRestoreSelection();
        }
    }, [presetStylesCategory, presetSettings]);

    const verifyAndRestoreSelection = useCallback(() => {
        if (presetSettings.length === 0 || !reduxSelectedSettingId) return;

        const selectedPreset = presetSettings.find(preset =>
            preset.savedSettingsId === reduxSelectedSettingId
        );

        if (selectedPreset) {
            const presetNameLower = selectedPreset.name.toLowerCase();
            let matchingTag = null;

            for (const [name, tag] of Object.entries(presetNameToTagMap)) {
                if (name === presetNameLower || presetNameLower.includes(name) || name.includes(presetNameLower)) {
                    matchingTag = tag;
                    break;
                }
            }

            if (matchingTag && !selectedFilters.includes(matchingTag)) {
                setSelectedFilters(prev => [...prev, matchingTag]);
            }
        } else {
            dispatch(setSelectedSettingId(''));
            setLocalSelectedPresetId(null);
        }
    }, [reduxSelectedSettingId, presetSettings, presetNameToTagMap, selectedFilters, dispatch, setSelectedFilters]);

    useEffect(() => {
        if (presetSettings.length > 0 && Object.keys(presetNameToTagMap).length > 0) {
            verifyAndRestoreSelection();
        }
    }, [presetSettings.length, verifyAndRestoreSelection, presetNameToTagMap]);

    useEffect(() => {
        if (dataLoaded && allCabinets.length > 0) {
            setRandomizedCabinets(shuffleArray([...allCabinets]));
            setTotalPages(Math.ceil(allCabinets.length / CARDS_PER_PAGE));
        }
    }, [dataLoaded, allCabinets]);

    useEffect(() => {
        const handleResize = () => {
            const windowWidth = window.innerWidth;
            setCardWidth(windowWidth <= 576 ? windowWidth - 70 : 350);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleQuantityChange = useCallback((id, value) => {
        setQuantities(prev => ({...prev, [id]: value}));
    }, []);

    const handleAddToCart = useCallback((cabinet) => {
        captureActivity(email, `Showroom: Add to Cart clicked (${cabinet?.serialNumber})`)
        const qty = quantities[cabinet.id] || 1;
        const uniqueId = nanoid();

        if (reduxSelectedSettingId) {
            const selectedPreset = presetSettings.find(preset =>
                preset.savedSettingsId === reduxSelectedSettingId
            );

            if (selectedPreset) {
                const cabinetWithPreset = {
                    ...cabinet,
                    quantity: parseInt(qty, 10),
                    id: uniqueId,
                    ...(cabinet?.caseEdge !== '0' && {
                        caseMaterial: selectedPreset?.caseMaterial,
                        ...(!cabinet?.serialNumber.startsWith('LP') && {
                            caseEdge: selectedPreset?.caseEdge
                        }),
                    }),
                    ...(cabinet?.frontEdge !== '0' && {
                        frontMaterial: selectedPreset?.frontMaterial,
                        ...(!cabinet?.serialNumber.startsWith('LP') && {
                            frontEdge: selectedPreset?.frontEdge,
                        })
                    }),
                    ...(cabinet?.jointControl === '1' && {
                        jointMethod: selectedPreset?.jointMethod
                    }),
                    ...(cabinet?.matBack === '1' && {
                        backPanelMaterial: selectedPreset?.backPanelMaterial,
                        backPanel: selectedPreset?.backPanel
                    }),
                    ...(cabinet?.doors === '1' && {
                        hingePlate: selectedPreset?.hingePlate,
                    }),
                    ...(cabinet?.drawers === '1' && {
                        drawerType: selectedPreset?.drawerType,
                    }),
                    ...(cabinet?.adjShelves === '1' && {
                        numOfShelves: selectedPreset?.numOfShelves
                    }),
                    ...(cabinet?.topDrwrHeight === '1' && {
                        topDrwrHeightValue: selectedPreset.topDrwrHeightValue,
                    }),
                    ...(cabinet?.filterTags.includes('Leg_Levelers') && {
                        includeLegLevelers: selectedPreset?.includeLegLevelers
                    }),
                    ...(cabinet?.includeLegLevelers === 'true' && {
                        bottomPanelConnector: 'Leg Levelers'
                    }),
                    presetApplied: selectedPreset.name,
                    settingsName: selectedPreset.name,
                    globalSettingsApplied: true
                };
                dispatch(addToCart(cabinetWithPreset));
            } else {
                console.error('Invalid preset ID in Redux store:', reduxSelectedSettingId);
                dispatch(setSelectedSettingId(''));
                dispatch(addToCart({
                    ...cabinet,
                    id: nanoid(),
                    quantity: parseInt(qty, 10)
                }));
            }
        } else {
            dispatch(addToCart({
                ...cabinet,
                id: nanoid(),
                quantity: parseInt(qty, 10)
            }));
        }
    }, [dispatch, quantities, reduxSelectedSettingId, presetSettings]);

    const handleConfigure = useCallback((item) => {
        captureActivity(email, `Showroom: Configure button clicked.`)
        const qty = quantities[item.id] || 1;
        const uniqueId = nanoid();

        if (reduxSelectedSettingId) {
            const selectedPreset = presetSettings.find(preset =>
                preset.savedSettingsId === reduxSelectedSettingId
            );

            if (selectedPreset) {
                const itemWithPreset = {
                    ...item,
                    quantity: qty,
                    id: uniqueId,
                    ...(item?.caseEdge !== '0' && {
                        caseMaterial: selectedPreset?.caseMaterial,
                        ...(!item?.serialNumber.startsWith('LP') && {
                            caseEdge: selectedPreset?.caseEdge
                        })
                    }),
                    ...(item?.frontEdge !== '0' && {
                        frontMaterial: selectedPreset?.frontMaterial,
                        ...(!item?.serialNumber.startsWith('LP') && {
                            frontEdge: selectedPreset?.frontEdge
                        })
                    }),
                    ...(item?.jointControl === '1' && {
                        jointMethod: selectedPreset?.jointMethod
                    }),
                    ...(item?.matBack === '1' && {
                        backPanelMaterial: selectedPreset?.backPanelMaterial,
                        backPanel: selectedPreset?.backPanel
                    }),
                    ...(item?.doors === '1' && {
                        hingePlate: selectedPreset?.hingePlate,
                    }),
                    ...(item?.drawers === '1' && {
                        drawerType: selectedPreset?.drawerType,
                    }),
                    ...(item?.adjShelves === '1' && {
                        numOfShelves: selectedPreset?.numOfShelves
                    }),
                    ...(item?.topDrwrHeight === '1' && {
                        topDrwrHeightValue: selectedPreset.topDrwrHeightValue,
                    }),
                    ...(item?.filterTags.includes('Leg_Levelers') && {
                        includeLegLevelers: selectedPreset?.includeLegLevelers
                    }),
                    ...(item?.includeLegLevelers === 'true' && {
                        bottomPanelConnector: 'Leg Levelers'
                    }),
                    ...(item?.serialNumber.startsWith('LP') && {
                        edgeBandingType: 'No Edgebanding'
                    }),
                    presetApplied: selectedPreset.name,
                    settingsName: selectedPreset.name,
                    globalSettingsApplied: true
                };
                // navigate('/order', {
                //     state: {
                //         items: [itemWithPreset],
                //         fromConfigureButton: true,
                //         uniqueId
                //     }
                // });
                navigate(`/cabinets/${itemWithPreset.serialNumber}`, {
                    state: {
                        items: [itemWithPreset],
                        fromConfigureButton: true,
                        uniqueId
                    }
                });
            } else {
                console.error('Invalid preset ID in Redux store:', reduxSelectedSettingId);
                dispatch(setSelectedSettingId(''));
                // navigate('/order', {
                navigate(`/cabinets/${item?.serialNumber}`, {
                    state: {
                        items: [{...item, quantity: qty, id: uniqueId}],
                        fromConfigureButton: true,
                        uniqueId
                    }
                });
            }
        } else {
            // navigate('/order', {
            navigate(`/cabinets/${item?.serialNumber}`, {
                state: {
                    items: [{...item, quantity: qty, id: uniqueId}],
                    fromConfigureButton: true,
                    uniqueId
                }
            });
        }
    }, [navigate, quantities, reduxSelectedSettingId, presetSettings, dispatch]);

    const handlePresetSelection = useCallback((filterTag) => {
        handleFilterSelection(filterTag);
        if (isFilterSelected(filterTag)) {
            dispatch(setSelectedSettingId(''));
            setLocalSelectedPresetId(null);
            return;
        }
        if (presetStylesCategory && presetSettings.length > 0) {
            let matchedPreset = null;
            let matchedDisplayName = null;
            for (const category of presetStylesCategory.subcategories) {
                for (const subcategory of category.subcategories) {
                    if (subcategory.tags && subcategory.tags.includes(filterTag)) {
                        matchedDisplayName = subcategory.displayName;
                        matchedPreset = presetSettings.find(preset =>
                            preset.name === matchedDisplayName ||
                            preset.name.toLowerCase() === matchedDisplayName.toLowerCase() ||
                            preset.name.toLowerCase().includes(matchedDisplayName.toLowerCase()) ||
                            matchedDisplayName.toLowerCase().includes(preset.name.toLowerCase())
                        );
                        break;
                    }
                }
                if (matchedPreset) break;
            }
            if (matchedPreset) {
                dispatch(setSelectedSettingId(matchedPreset.savedSettingsId));
                setLocalSelectedPresetId(matchedPreset.savedSettingsId);
            } else {
                dispatch(setSelectedSettingId(''));
                setLocalSelectedPresetId(null);
            }
        }
    }, [handleFilterSelection, isFilterSelected, presetStylesCategory, presetSettings, dispatch]);

    const renderFilterItem = useCallback((filterTag, displayName) => {
        if (hasCabinetsWithFilterTag(filterTag)) {
            return (
                <div className="sr-dropdown-item-wrapper" key={filterTag}>
                    <div
                        className={`sr-dropdown-item`}
                        onClick={() => {
                            captureActivity(email, `Filter Panel: Subcategory ${displayName} clicked`)
                            handleFilterSelection(filterTag)
                        }}
                    >
                        <div className="sr-dropdown-item-content">
                            <span className="sr-dropdown-item-text">{displayName}</span>
                            <div className="sr-dropdown-item-indicator">
                                <span
                                    className={`sr-dropdown-checkbox ${isFilterSelected(filterTag) ? 'checked' : ''}`}>
                                    {isFilterSelected(filterTag) && <span className="checkmark">✓</span>}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    }, [hasCabinetsWithFilterTag, isFilterSelected, handleFilterSelection]);

    const renderSubcategory = useCallback((category, subcategories) => {
        const hasChildrenWithFilterTags = subcategories.some(subcategory =>
            subcategory.tags.some(tag => hasCabinetsWithFilterTag(tag))
        );
        if (hasChildrenWithFilterTags) {
            const isExpanded = isCategoryExpanded(category);
            return (
                <div key={category} className="category-division">
                    <button
                        className={`btn btn-dark sr-dropdown-toggle w-100 text-start ${isExpanded ? 'expanded' : ''}`}
                        onClick={() =>
                            handleCategoryClick(category)
                        }
                    >
                        {category}
                    </button>
                    {isExpanded && (
                        <div className="sr-dropdown-subcontent">
                            {subcategories.map(subcategory =>
                                subcategory.tags.map(tag => renderFilterItem(tag, subcategory.displayName))
                            )}
                        </div>
                    )}
                </div>
            );
        }
        return null;
    }, [hasCabinetsWithFilterTag, handleCategoryClick, isCategoryExpanded, renderFilterItem]);

    const handleSearchSubmit = useCallback(() => {
        captureActivity(email, `Showroom: Search Query Submitted.`)
        if (searchQuery.trim() !== "") {
        } else {
            setSearchedProducts([]);
            setTotalPages(Math.ceil(filteredCabinets.length / CARDS_PER_PAGE));
        }
    }, [searchQuery, filteredCabinets]);

    const displayedProducts = useMemo(() => {
        if (searchQuery.trim() !== "" && searchedProducts.length > 0) {
            return searchedProducts;
        } else {
            return filteredCabinets;
        }
    }, [searchQuery, searchedProducts, filteredCabinets]);

    const paginatedProducts = useMemo(() => {
        const startIndex = (currentPage - 1) * CARDS_PER_PAGE;
        return displayedProducts.slice(startIndex, startIndex + CARDS_PER_PAGE);
    }, [currentPage, displayedProducts]);

    const handlePageChange = useCallback((pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({top: 0, behavior: "instant"});
    }, []);

    const handleSearchChange = useCallback((event) => {
        startTransition(() => {
            setSearchQuery(event.target.value);
        });
    }, []);


    const handleClearFilters = useCallback(() => {
        captureActivity(email, 'Filter Panel: Clear Filters clicked');
        setSelectedFilters([]);
        dispatch(setSelectedSettingId(''));
        setLocalSelectedPresetId(null);
    }, [setSelectedFilters, dispatch, email, captureActivity]);

    useEffect(() => {
        setTotalPages(Math.ceil(displayedProducts.length / CARDS_PER_PAGE));
    }, [displayedProducts]);

    const handleFilterPanelLoad = useCallback(() => {
        setIsFilterPanelLoaded(true);
    }, []);

    if (loading) return <LoadingPage/>;
    if (error) return <div>Error: {error}</div>;

    return (

        <div className="showroom-page min-vh-100">
            <SEO
                title="Premium Kitchen Cabinets & RTA Cabinet Solutions"
                description="Shop premium kitchen cabinets, pantry cabinets, and ready-to-assemble (RTA) cabinets at The Sealab."
                keywords={keywords}
                canonical="https://www.thesealab.com/showroom"
            />
            {/*<Helmet>*/}
            {/*    <title>Kitchen & Closet Cabinet Collection | The Sealab</title>*/}
            {/*    <meta*/}
            {/*        name="description"*/}
            {/*        content="Discover high-quality kitchen and closet cabinets at The Sealab. Shop custom CNC cabinets in NYC. Perfect for home renovations."*/}
            {/*    />*/}
            {/*    <meta*/}
            {/*        name="keywords"*/}
            {/*        content="kitchen cabinets, closet cabinets, CNC cabinets, high-quality cabinets, cabinets near me, cabinets in Brooklyn, The Sealab, order cabinets online, cabinets in NYC"*/}
            {/*    />*/}
            {/*    <meta property="og:title" content="Showroom | The Sealab - Kitchen & Closet Cabinets"/>*/}
            {/*    <meta*/}
            {/*        property="og:description"*/}
            {/*        content="Explore the finest collection of kitchen and closet cabinets at The Sealab. Find your ideal cabinet."*/}
            {/*    />*/}
            {/*    <meta property="og:url" content="https://www.thesealab.com/showroom"/>*/}
            {/*    <meta property="og:type" content="website"/>*/}
            {/*    <meta property="og:site_name" content="The Sealab"/>*/}
            {/*    <link rel="preconnect" href="https://www.thesealab.com"/>*/}
            {/*    <link rel="dns-prefetch" href="https://www.thesealab.com"/>*/}
            {/*    {paginatedProducts.slice(0, 8).map((cabinet, index) => (*/}
            {/*        <link*/}
            {/*            key={index}*/}
            {/*            rel="preload"*/}
            {/*            as="image"*/}
            {/*            href={`${process.env.REACT_APP_IMAGEURL}${cabinet.serialNumber}.webp`}*/}
            {/*            fetchPriority="high"*/}
            {/*            media="(min-width: 1200px)"*/}
            {/*        />*/}
            {/*    ))}*/}
            {/*    {paginatedProducts.slice(0, 8).map((cabinet, index) => (*/}
            {/*        <link*/}
            {/*            key={`mobile-${index}`}*/}
            {/*            rel="preload"*/}
            {/*            as="image"*/}
            {/*            href={`${process.env.REACT_APP_IMAGEURL}${cabinet.serialNumber}.webp`}*/}
            {/*            fetchPriority="high"*/}
            {/*            media="(max-width: 1199px)"*/}
            {/*        />*/}
            {/*    ))}*/}
            {/*</Helmet>*/}

            {dataLoaded && (
                <div className="showroom-container">
                    <Suspense fallback={<FilterPanelSkeleton/>}>
                        <FilterPanel
                            hasCabinetsWithFilterTag={hasCabinetsWithFilterTag}
                            handleCategoryClick={handleCategoryClick}
                            isCategoryExpanded={isCategoryExpanded}
                            renderFilterItem={renderFilterItem}
                            renderSubcategory={renderSubcategory}
                            onLoad={handleFilterPanelLoad}
                            selectedFilters={selectedFilters}
                            onClearFilters={handleClearFilters}
                        />
                    </Suspense>
                    <div className="showroom-content">
                        <div className="upload-sketch-text">
                            Can't find what you're looking for? Contact us{' '}
                            <a className="hyperlink" href="/customer-support/contact"
                               onClick={() => captureActivity(email, `Showroom: Clicked on 'Contact us here'`)}>
                                here
                            </a>{' '}
                            and upload a sketch
                        </div>
                        <div className="showroom-quote-warning">
                            Please note, to receive a free quote you must complete the checkout process. Pricing
                            will be available within one business day.
                        </div>
                        <Suspense fallback={<div className="search-skeleton"></div>}>
                            <SearchBar
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onSubmit={handleSearchSubmit}
                            />
                        </Suspense>
                        {presetStylesCategory && (
                            <Suspense fallback={<div className="preset-skeleton"></div>}>
                                <PresetStylesBar
                                    presetStyles={presetStylesCategory}
                                    isFilterSelected={isFilterSelected}
                                    handleFilterSelection={handlePresetSelection}
                                />
                            </Suspense>
                        )}
                        {/*{displayedProducts.length === 0 ? (*/}
                        {/*    <div className="showroom-no-results">*/}
                        {/*        No cabinets found matching your search criteria. Please try our <a href="#">Advanced*/}
                        {/*        Search via Chatbot</a>*/}
                        {/*    </div>*/}
                        {/*) : (*/}
                        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4">
                            {paginatedProducts.map((cabinet, index) => (
                                <div key={index} className="col">
                                    <Suspense fallback={<div className="cabinet-card-skeleton"></div>}>
                                        <CabinetCard
                                            cabinet={cabinet}
                                            cardWidth={cardWidth}
                                            onQuantityChange={handleQuantityChange}
                                            onConfigure={handleConfigure}
                                            onAddToCart={handleAddToCart}
                                            isPriority={index < 8}
                                            presetStyle={presetStylesCategory}
                                        />
                                    </Suspense>
                                </div>
                            ))}
                        </div>
                        {/*)}*/}
                        {displayedProducts.length > 0 && (
                            <Suspense fallback={<div className="pagination-skeleton"></div>}>
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                />
                            </Suspense>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShowRoomComponent;